import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import logo from "./RoyalLogo.png";
import studentAvatar from "./goldavartar.png";

/* ============================================================================
   ROYAL PRIESTHOOD ACADEMY
   PROFESSIONAL ANNUAL STUDENT REPORT
   A4 LANDSCAPE | ROYAL BLUE | SKY BLUE | WHITE
============================================================================ */

const sem1Periods = ["Period 1", "Period 2", "Period 3", "Exam"];
const sem2Periods = ["Period 4", "Period 5", "Period 6", "Exam2"];

const SUBJECT_ORDER = {
  ECE: [
    "Health Science",
    "History",
    "English Writing",
    "English Reciting",
    "Mathematic Reciting",
    "Mathematic Writing",
    "Reading",
    "Social Studies",
    "Phonics",
    "Bible/RME",
    "Spelling",
    "Arts/Drawing",
    "General Science",
  ],
  Elementary: [
    "Math",
    "English",
    "Reading",
    "Writing",
    "Phonics",
    "Spelling",
    "General Science",
    "Social Studies",
    "Bible/RME",
    "Physical Education",
    "Arts/Drawing",
    "Health Science",
  ],
};

/* ============================================================================
   LIGHT PROFESSIONAL SCHOOL COLOR SYSTEM
============================================================================ */

const COLORS = {
  royalBlue: "#174A8B",
  deepBlue: "#12396A",
  blue: "#2878C8",
  skyBlue: "#77BDF2",
  paleBlue: "#EAF5FD",
  lightBlue: "#F4F9FE",
  blueBorder: "#B9D9F2",
  blueTint: "#DCEEFF",

  white: "#FFFFFF",
  paper: "#FFFFFF",
  black: "#111827",
  charcoal: "#293548",
  gray: "#667085",
  mediumGray: "#98A2B3",
  lightGray: "#F5F7FA",
  border: "#D8E0E9",
  divider: "#E7ECF2",

  green: "#237A50",
  lightGreen: "#EAF6EF",
  greenBorder: "#B9DEC8",

  red: "#B42318",
  lightRed: "#FDEDEC",

  gold: "#C9A44C",
  lightGold: "#FFF8E6",
  goldBorder: "#E7D39D",
};

/* ============================================================================
   SCORE HELPERS
============================================================================ */

const isMissing = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  String(value).trim().toUpperCase() === "N/A";

const getColor = (value) => {
  if (isMissing(value)) return COLORS.mediumGray;

  const textValue = String(value).trim().toUpperCase();

  if (textValue === "F") return COLORS.red;

  if (["A", "B", "C", "D", "E"].includes(textValue)) {
    return COLORS.royalBlue;
  }

  const numericValue = Number(value);

  if (!Number.isNaN(numericValue)) {
    if (numericValue < 70) return COLORS.red;
    if (numericValue >= 90) return COLORS.green;
    return COLORS.royalBlue;
  }

  return COLORS.charcoal;
};

const getScoreBackground = (value) => {
  if (isMissing(value)) return COLORS.lightGray;

  const numericValue = Number(value);

  if (!Number.isNaN(numericValue)) {
    if (numericValue < 70) return COLORS.lightRed;
    if (numericValue >= 90) return COLORS.lightGreen;
  }

  return COLORS.lightBlue;
};

const formatValue = (value) =>
  value === undefined || value === null || value === ""
    ? "N/A"
    : value;

const safeAverage = (value) => formatValue(value);

/* ============================================================================
   PDF STYLES
============================================================================ */

const styles = StyleSheet.create({
  /* PAGE */

  page: {
    paddingTop: 15,
    paddingHorizontal: 20,
    paddingBottom: 14,
    backgroundColor: COLORS.paper,
    fontFamily: "Helvetica",
    fontSize: 8,
    color: COLORS.black,
  },

  backPage: {
    paddingTop: 15,
    paddingHorizontal: 20,
    paddingBottom: 14,
    backgroundColor: COLORS.paper,
    fontFamily: "Helvetica",
    fontSize: 8,
    color: COLORS.black,
  },

  topAccentBar: {
    height: 4,
    backgroundColor: COLORS.royalBlue,
    borderRadius: 2,
    marginBottom: 7,
  },

  /* SCHOOL HEADER */

  header: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.blueBorder,
    borderRadius: 7,
    paddingVertical: 7,
    paddingHorizontal: 10,
    minHeight: 61,
  },

  logoSection: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  logo: {
    width: 51,
    height: 45,
    objectFit: "contain",
    marginRight: 9,
  },

  schoolIdentity: {
    flex: 1,
  },

  schoolName: {
    fontSize: 13,
    fontWeight: "bold",
    color: COLORS.deepBlue,
    marginBottom: 3,
  },

  schoolSubtitle: {
    fontSize: 7,
    color: COLORS.royalBlue,
    marginBottom: 3,
  },

  schoolLocation: {
    fontSize: 6.8,
    color: COLORS.gray,
  },

  reportTitleBlock: {
    alignItems: "flex-end",
    justifyContent: "center",
    marginLeft: 8,
    marginRight: 12,
  },

  reportCardTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: COLORS.royalBlue,
    textAlign: "right",
    marginBottom: 4,
  },

  reportCardSubtitle: {
    fontSize: 6.7,
    color: COLORS.gray,
    textAlign: "right",
  },

  headerAvatar: {
    width: 47,
    height: 47,
    borderRadius: 24,
    objectFit: "cover",
    borderWidth: 2,
    borderColor: COLORS.skyBlue,
  },

  /* STUDENT DETAILS */

  studentInfoBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.lightBlue,
    borderWidth: 0.7,
    borderColor: COLORS.blueBorder,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.royalBlue,
    borderRadius: 4,
    paddingVertical: 6,
    paddingHorizontal: 9,
    marginTop: 6,
    marginBottom: 6,
  },

  studentInfoItem: {
    flex: 1,
    paddingRight: 7,
  },

  studentInfoLabel: {
    fontSize: 6,
    color: COLORS.gray,
    marginBottom: 3,
    textTransform: "uppercase",
  },

  studentInfoValue: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.black,
  },

  /* SECTION HEADERS */

  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: COLORS.paleBlue,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.royalBlue,
    borderWidth: 0.7,
    borderColor: COLORS.blueBorder,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginTop: 3,
    marginBottom: 4,
    borderRadius: 3,
  },

  sectionTitle: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.deepBlue,
  },

  sectionTag: {
    fontSize: 6,
    color: COLORS.gray,
    fontWeight: "bold",
  },

  /* ACADEMIC TABLE */

  academicTableWrapper: {
    flexDirection: "row",
    width: "100%",
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderRadius: 3,
    overflow: "hidden",
  },

  academicHalfTable: {
    width: "50%",
  },

  academicHalfTableRight: {
    width: "50%",
    borderLeftWidth: 2,
    borderLeftColor: COLORS.white,
  },

  academicHeaderRow: {
    flexDirection: "row",
    minHeight: 23,
    backgroundColor: COLORS.paleBlue,
  },

  academicBodyRow: {
    flexDirection: "row",
    minHeight: 17,
  },

  academicSubjectCell: {
    width: "50%",
    minHeight: 17,
    justifyContent: "center",
    paddingHorizontal: 4,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: COLORS.border,
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },

  academicSubjectHeaderCell: {
    width: "50%",
    minHeight: 23,
    justifyContent: "center",
    paddingHorizontal: 4,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.7,
    borderColor: COLORS.blueBorder,
    fontSize: 6.8,
    fontWeight: "bold",
    color: COLORS.deepBlue,
  },

  academicScoreCell: {
    width: "12.5%",
    minHeight: 17,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingHorizontal: 1,
    paddingVertical: 1,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: COLORS.border,
    fontSize: 7.5,
    color: COLORS.charcoal,
  },

  academicScoreHeaderCell: {
    width: "12.5%",
    minHeight: 23,
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    paddingHorizontal: 1,
    paddingVertical: 2,
    borderRightWidth: 0.5,
    borderBottomWidth: 0.7,
    borderColor: COLORS.blueBorder,
    fontSize: 6.2,
    fontWeight: "bold",
    color: COLORS.deepBlue,
  },

  semesterAverageCell: {
    backgroundColor: COLORS.paleBlue,
    fontWeight: "bold",
  },

  yearlyAverageCell: {
    backgroundColor: COLORS.lightGold,
    fontWeight: "bold",
  },

  /* PERIOD AVERAGES */

  averageLabel: {
    fontSize: 5.5,
    color: COLORS.gray,
    marginBottom: 1,
  },

  averageText: {
    fontSize: 7,
    fontWeight: "bold",
  },

  /* PERFORMANCE SUMMARY */

  performanceSummary: {
    flexDirection: "row",
    width: "100%",
    marginTop: 6,
    marginBottom: 1,
  },

  summaryCard: {
    flex: 1,
    marginRight: 5,
    backgroundColor: COLORS.white,
    borderWidth: 0.8,
    borderColor: COLORS.blueBorder,
    borderTopWidth: 2,
    borderTopColor: COLORS.skyBlue,
    borderRadius: 4,
    paddingVertical: 5,
    alignItems: "center",
    justifyContent: "center",
  },

  summaryCardLast: {
    flex: 1,
    backgroundColor: COLORS.paleBlue,
    borderWidth: 0.8,
    borderColor: COLORS.royalBlue,
    borderTopWidth: 2,
    borderTopColor: COLORS.royalBlue,
    borderRadius: 4,
    paddingVertical: 5,
    alignItems: "center",
    justifyContent: "center",
  },

  summaryLabel: {
    fontSize: 6.2,
    color: COLORS.gray,
    marginBottom: 3,
  },

  summaryValue: {
    fontSize: 14,
    fontWeight: "bold",
  },

  /* POLICY AND REMARKS */

  promotionBox: {
    backgroundColor: COLORS.lightBlue,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.skyBlue,
    paddingVertical: 4,
    paddingHorizontal: 7,
    marginTop: 5,
    borderRadius: 3,
  },

  promotionTitle: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.royalBlue,
    marginBottom: 2,
  },

  promotionText: {
    fontSize: 6.2,
    color: COLORS.charcoal,
    lineHeight: 1.3,
  },

  remarkBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.paleBlue,
    borderWidth: 0.7,
    borderColor: COLORS.blueBorder,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.royalBlue,
    paddingVertical: 5,
    paddingHorizontal: 7,
    marginTop: 4,
    borderRadius: 3,
  },

  remarkLabel: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.deepBlue,
    marginRight: 5,
  },

  remarkText: {
    flex: 1,
    fontSize: 6.5,
    color: COLORS.charcoal,
  },

  gradeLegend: {
    backgroundColor: COLORS.white,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    padding: 4,
    marginTop: 4,
    textAlign: "center",
    fontSize: 6,
    color: COLORS.charcoal,
    borderRadius: 3,
  },

  /* CONDUCT AND RANK */

  conductRankBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 4,
    paddingVertical: 3,
  },

  conductText: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },

  rankBadge: {
    backgroundColor: COLORS.paleBlue,
    borderWidth: 0.8,
    borderColor: COLORS.blueBorder,
    borderRadius: 3,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },

  rankText: {
    color: COLORS.deepBlue,
    fontSize: 7,
    fontWeight: "bold",
  },

  /* SIGNATURES */

  signatureSection: {
    flexDirection: "row",
    justifyContent: "space-evenly",
    backgroundColor: COLORS.white,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderRadius: 4,
    paddingVertical: 5,
    paddingHorizontal: 12,
    marginTop: 4,
  },

  signatureItem: {
    width: "34%",
    alignItems: "center",
  },

  signatureLabel: {
    fontSize: 6,
    color: COLORS.gray,
    marginBottom: 10,
  },

  signatureLine: {
    width: "100%",
    borderTopWidth: 0.8,
    borderTopColor: COLORS.charcoal,
    marginBottom: 3,
  },

  signatureDate: {
    fontSize: 5.5,
    color: COLORS.gray,
  },

  /* BACK PAGE HEADER */

  backPageHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: COLORS.white,
    borderWidth: 0.8,
    borderColor: COLORS.blueBorder,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.royalBlue,
    borderRadius: 5,
    padding: 7,
    marginBottom: 7,
  },

  backPageHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },

  backPageLogo: {
    width: 43,
    height: 39,
    objectFit: "contain",
    marginRight: 8,
  },

  backPageTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.deepBlue,
  },

  backPageSubtitle: {
    fontSize: 6.5,
    color: COLORS.gray,
    marginTop: 3,
  },

  backPageTag: {
    backgroundColor: COLORS.paleBlue,
    color: COLORS.royalBlue,
    fontSize: 6.5,
    fontWeight: "bold",
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 3,
  },

  backColumns: {
    flexDirection: "row",
    width: "100%",
    flex: 1,
  },

  backColumnLeft: {
    width: "49%",
    marginRight: "2%",
  },

  backColumnRight: {
    width: "49%",
  },

  /* BACK PAGE CARDS */

  card: {
    backgroundColor: COLORS.white,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderRadius: 5,
    padding: 7,
    marginBottom: 6,
  },

  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.paleBlue,
    borderBottomWidth: 0.7,
    borderBottomColor: COLORS.blueBorder,
    marginHorizontal: -7,
    marginTop: -7,
    marginBottom: 6,
    paddingHorizontal: 7,
    paddingVertical: 5,
  },

  cardHeaderAccent: {
    width: 3,
    height: 12,
    backgroundColor: COLORS.royalBlue,
    marginRight: 6,
    borderRadius: 1,
  },

  cardTitle: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.deepBlue,
  },

  /* SCHOOL PROFILE */

  schoolProfileContent: {
    alignItems: "center",
    justifyContent: "center",
  },

  schoolProfileLogo: {
    width: 44,
    height: 44,
    objectFit: "contain",
    marginBottom: 4,
  },

  schoolProfileName: {
    fontSize: 9,
    fontWeight: "bold",
    color: COLORS.deepBlue,
    textAlign: "center",
    marginBottom: 3,
  },

  schoolProfileLocation: {
    fontSize: 6.5,
    color: COLORS.gray,
    textAlign: "center",
    lineHeight: 1.3,
    marginBottom: 4,
  },

  /* STUDENT PROFILE */

  studentProfileRow: {
    flexDirection: "row",
    alignItems: "center",
  },

  avatar: {
    width: 57,
    height: 57,
    borderRadius: 29,
    objectFit: "cover",
    marginRight: 9,
    borderWidth: 2,
    borderColor: COLORS.skyBlue,
  },

  studentProfileDetails: {
    flex: 1,
  },

  profileStudentName: {
    fontSize: 10,
    fontWeight: "bold",
    color: COLORS.deepBlue,
    marginBottom: 4,
  },

  profileDetailRow: {
    flexDirection: "row",
    marginBottom: 3,
  },

  profileDetailLabel: {
    width: 68,
    fontSize: 6.5,
    color: COLORS.gray,
  },

  profileDetailValue: {
    flex: 1,
    fontSize: 6.7,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },

  /* PERFORMANCE CIRCLES */

  averageSummaryRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
  },

  averageSummaryItem: {
    flex: 1,
    alignItems: "center",
  },

  averageSummaryCircle: {
    width: 49,
    height: 49,
    borderRadius: 25,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: COLORS.skyBlue,
    backgroundColor: COLORS.lightBlue,
  },

  averageSummaryCircleOrange: {
    width: 49,
    height: 49,
    borderRadius: 25,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: COLORS.blue,
    backgroundColor: COLORS.paleBlue,
  },

  averageSummaryCircleGreen: {
    width: 49,
    height: 49,
    borderRadius: 25,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1.5,
    borderColor: COLORS.green,
    backgroundColor: COLORS.lightGreen,
  },

  averageSummaryLabel: {
    fontSize: 5.8,
    textAlign: "center",
    color: COLORS.gray,
    marginTop: 4,
  },

  averageSummaryValue: {
    fontSize: 9,
    fontWeight: "bold",
  },

  /* PROMOTION OPTIONS */

  promotionOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.divider,
  },

  promotionLetter: {
    width: 17,
    height: 17,
    borderRadius: 9,
    backgroundColor: COLORS.paleBlue,
    borderWidth: 0.8,
    borderColor: COLORS.blueBorder,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 6,
  },

  promotionLetterText: {
    fontSize: 6.5,
    fontWeight: "bold",
    color: COLORS.royalBlue,
  },

  promotionOptionText: {
    flex: 1,
    fontSize: 6.7,
    color: COLORS.charcoal,
  },

  promotionLine: {
    width: 100,
    borderBottomWidth: 0.7,
    borderBottomColor: COLORS.charcoal,
    marginLeft: 4,
  },

  /* PERIOD AVERAGES */

  periodAverageRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 3,
    paddingHorizontal: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.divider,
  },

  periodAverageName: {
    fontSize: 6.7,
    color: COLORS.charcoal,
  },

  periodAverageValue: {
    fontSize: 7,
    fontWeight: "bold",
  },

  /* INFORMATION GRID */

  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },

  infoGridItem: {
    width: "50%",
    paddingVertical: 3,
    paddingRight: 4,
  },

  infoGridLabel: {
    fontSize: 5.8,
    color: COLORS.gray,
    marginBottom: 2,
  },

  infoGridValue: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.charcoal,
  },

  /* PARENT SIGNATURE */

  parentSignatureCard: {
    backgroundColor: COLORS.lightBlue,
    borderWidth: 0.8,
    borderColor: COLORS.blueBorder,
    borderRadius: 5,
    padding: 7,
    alignItems: "center",
  },

  parentSignatureTitle: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.deepBlue,
    marginBottom: 13,
  },

  parentSignatureLine: {
    width: "75%",
    borderTopWidth: 0.8,
    borderTopColor: COLORS.charcoal,
    marginBottom: 3,
  },

  parentSignatureCaption: {
    fontSize: 6,
    color: COLORS.gray,
  },

  /* BACK FOOTER */

  backFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: COLORS.paleBlue,
    borderTopWidth: 1,
    borderTopColor: COLORS.blueBorder,
    paddingVertical: 5,
    paddingHorizontal: 7,
    borderRadius: 3,
    marginTop: 2,
  },

  backFooterText: {
    flex: 1,
    color: COLORS.gray,
    fontSize: 5.5,
    marginRight: 8,
  },

  backFooterHighlight: {
    color: COLORS.royalBlue,
    fontSize: 5.8,
    fontWeight: "bold",
  },
});

/* ============================================================================
   REPORT CARD PDF COMPONENT
============================================================================ */

const ReportCardPDF = ({
  responses = [],
  selectedGradeClass,
  selectedYear,
  showRemarksInPDF,
}) => {
  return (
    <Document>
      {responses.map((res, idx) => {
        if (!res?.data?.period_grades) return null;

        const data = res.data;

        const gradeClassName = data.grade_class_name || "";

        const division =
          gradeClassName.toLowerCase().includes("nursery") ||
          gradeClassName.toLowerCase().includes("kindergarten")
            ? "ECE"
            : "Elementary";

        const order = SUBJECT_ORDER[division] || [];

        const dataSubjects = [
          ...new Set(
            data.period_grades
              .map((g) => g.subject_name?.trim())
              .filter(Boolean)
          ),
        ];

        const extraSubjects = dataSubjects.filter(
          (subject) =>
            !order.some(
              (orderedSubject) =>
                orderedSubject.toLowerCase() === subject.toLowerCase()
            )
        );

        const subjects = [
          ...order.filter((subject) =>
            dataSubjects.some(
              (dataSubject) =>
                dataSubject.toLowerCase() === subject.toLowerCase()
            )
          ),
          ...extraSubjects,
        ];

        const getSubjectAverage = (subject, field) =>
          data.subjects_semester_averages?.find(
            (item) =>
              item.subject_name?.toLowerCase() === subject.toLowerCase()
          )?.[field] ?? "N/A";

        const getSubjectGrades = (subject) =>
          data.period_grades.filter(
            (grade) =>
              grade.subject_name?.toLowerCase() === subject.toLowerCase()
          );

        const allPeriods = [...sem1Periods, ...sem2Periods];

        const renderScore = (score, key, extraStyle = {}) => (
          <Text
            key={key}
            style={[
              styles.academicScoreCell,
              {
                color: getColor(score),
                backgroundColor: getScoreBackground(score),
                fontWeight: "bold",
              },
              extraStyle,
            ]}
          >
            {formatValue(score)}
          </Text>
        );

        const renderAverageCell = (label, value, key) => (
          <View key={key} style={styles.academicScoreCell}>
            <Text style={styles.averageLabel}>{label}</Text>
            <Text
              style={[
                styles.averageText,
                { color: getColor(value) },
              ]}
            >
              {formatValue(value)}
            </Text>
          </View>
        );

        const studentImage = data.student_admission_image || studentAvatar;

        return (
          <React.Fragment key={idx}>
            {/* ============================================================
                PAGE 1 — ANNUAL ACADEMIC REPORT
            ============================================================ */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.page}
              wrap={false}
            >
              <View style={styles.topAccentBar} />

              {/* SCHOOL IDENTITY */}

              <View style={styles.header}>
                <View style={styles.logoSection}>
                  <Image src={logo} style={styles.logo} />

                  <View style={styles.schoolIdentity}>
                    <Text style={styles.schoolName}>
                      Royal Priesthood Academy
                    </Text>

                    <Text style={styles.schoolSubtitle}>
                      Academic Excellence • Character • Faith • Discipline
                    </Text>

                    <Text style={styles.schoolLocation}>
                      Zazay Community, Paynesville City, Monrovia, Liberia
                    </Text>
                  </View>
                </View>

                <View style={styles.reportTitleBlock}>
                  <Text style={styles.reportCardTitle}>
                    STUDENT REPORT CARD
                  </Text>

                  <Text style={styles.reportCardSubtitle}>
                    OFFICIAL ANNUAL ACADEMIC RECORD
                  </Text>
                </View>

                <Image src={studentImage} style={styles.headerAvatar} />
              </View>

              {/* STUDENT INFORMATION */}

              <View style={styles.studentInfoBar}>
                {[
                  ["STUDENT NAME", data.student_name || "N/A"],
                  ["GRADE / CLASS", data.grade_class_name || "N/A"],
                  ["ACADEMIC YEAR", selectedYear?.name || "N/A"],
                  ["DIVISION", division],
                ].map(([label, value]) => (
                  <View key={label} style={styles.studentInfoItem}>
                    <Text style={styles.studentInfoLabel}>{label}</Text>
                    <Text style={styles.studentInfoValue}>{value}</Text>
                  </View>
                ))}
              </View>

              {/* PERFORMANCE TABLE HEADING */}

              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>
                  ANNUAL ACADEMIC PERFORMANCE
                </Text>

                <Text style={styles.sectionTag}>
                  SEMESTER 1 • SEMESTER 2 • YEARLY RESULTS
                </Text>
              </View>

              {/* MAIN ACADEMIC TABLE */}

              <View style={styles.academicTableWrapper}>
                {/* SEMESTER 1 */}

                <View style={styles.academicHalfTable}>
                  <View style={styles.academicHeaderRow}>
                    <Text style={styles.academicSubjectHeaderCell}>
                      SUBJECTS
                    </Text>

                    {sem1Periods.map((period) => (
                      <Text key={period} style={styles.academicScoreHeaderCell}>
                        {period}
                      </Text>
                    ))}

                    <Text
                      style={[
                        styles.academicScoreHeaderCell,
                        { backgroundColor: COLORS.blueTint },
                      ]}
                    >
                      SA1
                    </Text>
                  </View>

                  {subjects.map((subject, rowIndex) => {
                    const grades = getSubjectGrades(subject);
                    const sem1Avg = getSubjectAverage(
                      subject,
                      "semester_1_avg"
                    );

                    return (
                      <View
                        key={`sem1-${subject}`}
                        style={[
                          styles.academicBodyRow,
                          {
                            backgroundColor:
                              rowIndex % 2 === 0
                                ? COLORS.white
                                : COLORS.lightGray,
                          },
                        ]}
                      >
                        <Text style={styles.academicSubjectCell}>
                          {subject}
                        </Text>

                        {sem1Periods.map((period) => {
                          const grade = grades.find(
                            (item) => item.period_name === period
                          );

                          const score = formatValue(
                            grade?.formatted_final_score
                          );

                          return renderScore(
                            score,
                            `${subject}-${period}-left`,
                            { fontSize: score === "N/A" ? 6 : 7.5 }
                          );
                        })}

                        {renderScore(
                          sem1Avg,
                          `${subject}-sa1`,
                          {
                            backgroundColor: COLORS.paleBlue,
                            fontWeight: "bold",
                          }
                        )}
                      </View>
                    );
                  })}
                </View>

                {/* SEMESTER 2 */}

                <View
                  style={[
                    styles.academicHalfTable,
                    styles.academicHalfTableRight,
                  ]}
                >
                  <View style={styles.academicHeaderRow}>
                    {sem2Periods.map((period) => (
                      <Text key={period} style={styles.academicScoreHeaderCell}>
                        {period}
                      </Text>
                    ))}

                    <Text
                      style={[
                        styles.academicScoreHeaderCell,
                        { backgroundColor: COLORS.blueTint },
                      ]}
                    >
                      SA2
                    </Text>

                    <Text
                      style={[
                        styles.academicScoreHeaderCell,
                        {
                          backgroundColor: COLORS.lightGold,
                          color: COLORS.deepBlue,
                        },
                      ]}
                    >
                      YEARLY
                    </Text>
                  </View>

                  {subjects.map((subject, rowIndex) => {
                    const grades = getSubjectGrades(subject);
                    const sem2Avg = getSubjectAverage(
                      subject,
                      "semester_2_avg"
                    );
                    const yearlyAvg = getSubjectAverage(
                      subject,
                      "yearly_avg"
                    );

                    return (
                      <View
                        key={`sem2-${subject}`}
                        style={[
                          styles.academicBodyRow,
                          {
                            backgroundColor:
                              rowIndex % 2 === 0
                                ? COLORS.white
                                : COLORS.lightGray,
                          },
                        ]}
                      >
                        {sem2Periods.map((period) => {
                          const grade = grades.find(
                            (item) => item.period_name === period
                          );

                          const score = formatValue(
                            grade?.formatted_final_score
                          );

                          return renderScore(
                            score,
                            `${subject}-${period}-right`,
                            { fontSize: score === "N/A" ? 6 : 7.5 }
                          );
                        })}

                        {renderScore(
                          sem2Avg,
                          `${subject}-sa2`,
                          {
                            backgroundColor: COLORS.paleBlue,
                            fontWeight: "bold",
                          }
                        )}

                        {renderScore(
                          yearlyAvg,
                          `${subject}-yearly`,
                          {
                            backgroundColor: COLORS.lightGold,
                            fontWeight: "bold",
                          }
                        )}
                      </View>
                    );
                  })}
                </View>
              </View>

              {/* PERIOD AVERAGES */}

              <View
                style={[
                  styles.academicTableWrapper,
                  { marginTop: 0 },
                ]}
              >
                <View style={styles.academicHalfTable}>
                  <View
                    style={[
                      styles.academicBodyRow,
                      { backgroundColor: COLORS.lightGray },
                    ]}
                  >
                    <Text style={styles.academicSubjectCell}>
                      PERIOD AVERAGES
                    </Text>

                    {sem1Periods.map((period) =>
                      renderScore(
                        data.period_average?.[period] ?? "N/A",
                        `period-average-${period}`
                      )
                    )}

                    {renderAverageCell(
                      "SA1",
                      data.semester_1_avg,
                      "semester-average-1"
                    )}
                  </View>
                </View>

                <View
                  style={[
                    styles.academicHalfTable,
                    styles.academicHalfTableRight,
                  ]}
                >
                  <View
                    style={[
                      styles.academicBodyRow,
                      { backgroundColor: COLORS.lightGray },
                    ]}
                  >
                    {sem2Periods.map((period) =>
                      renderScore(
                        data.period_average?.[period] ?? "N/A",
                        `period-average-${period}`
                      )
                    )}

                    {renderAverageCell(
                      "SA2",
                      data.semester_2_avg,
                      "semester-average-2"
                    )}

                    {renderAverageCell(
                      "YEARLY",
                      data.yearly_average,
                      "yearly-average"
                    )}
                  </View>
                </View>
              </View>

              {/* SUMMARY CARDS */}

              <View style={styles.performanceSummary}>
                <View style={styles.summaryCard}>
                  <Text style={styles.summaryLabel}>
                    SEMESTER 1 AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      { color: getColor(data.semester_1_avg) },
                    ]}
                  >
                    {safeAverage(data.semester_1_avg)}
                  </Text>
                </View>

                <View style={styles.summaryCard}>
                  <Text style={styles.summaryLabel}>
                    SEMESTER 2 AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      { color: getColor(data.semester_2_avg) },
                    ]}
                  >
                    {safeAverage(data.semester_2_avg)}
                  </Text>
                </View>

                <View style={styles.summaryCardLast}>
                  <Text style={styles.summaryLabel}>
                    FINAL YEARLY AVERAGE
                  </Text>

                  <Text
                    style={[
                      styles.summaryValue,
                      { color: getColor(data.yearly_average) },
                    ]}
                  >
                    {safeAverage(data.yearly_average)}
                  </Text>
                </View>
              </View>

              {/* PROMOTION POLICY */}

              <View style={styles.promotionBox}>
                <Text style={styles.promotionTitle}>
                  PROMOTION POLICY
                </Text>

                <Text style={styles.promotionText}>
                  A student who fails two major subjects may not be eligible
                  for promotion. A student who fails one major subject may be
                  required to attend vacation school or complete an approved
                  academic intervention program.
                </Text>
              </View>

              {/* FINAL REMARK */}

              <View style={styles.remarkBox}>
                <Text style={styles.remarkLabel}>FINAL REMARK:</Text>

                <Text style={styles.remarkText}>
                  {showRemarksInPDF
                    ? data.remark || "No remark provided."
                    : "Year-End Remark will appear here after review."}
                </Text>
              </View>

              {/* GRADING SCALE */}

              <Text style={styles.gradeLegend}>
                GRADING SCALE: {"  "}
                60–69 FAIL {"  |  "}
                70–79 FAIR {"  |  "}
                80–89 GOOD {"  |  "}
                90–95 VERY GOOD {"  |  "}
                96–100 EXCELLENT
              </Text>

              {/* CONDUCT AND CLASS RANK */}

              <View style={styles.conductRankBar}>
                <Text style={styles.conductText}>
                  YEARLY CONDUCT: {data.yearly_conduct || "N/A"}
                </Text>

                <View style={styles.rankBadge}>
                  <Text style={styles.rankText}>
                    CLASS RANK: {data.rank || "N/A"}
                  </Text>
                </View>
              </View>

              {/* SIGNATURES */}

              <View style={styles.signatureSection}>
                <View style={styles.signatureItem}>
                  <Text style={styles.signatureLabel}>
                    PRINCIPAL'S SIGNATURE
                  </Text>

                  <View style={styles.signatureLine} />

                  <Text style={styles.signatureDate}>
                    Date: ____________________
                  </Text>
                </View>

                <View style={styles.signatureItem}>
                  <Text style={styles.signatureLabel}>
                    CLASS SPONSOR'S SIGNATURE
                  </Text>

                  <View style={styles.signatureLine} />

                  <Text style={styles.signatureDate}>
                    Date: ____________________
                  </Text>
                </View>
              </View>
            </Page>

            {/* ============================================================
                PAGE 2 — STUDENT PROFILE AND PROMOTION RECORD
            ============================================================ */}

            <Page
              size="A4"
              orientation="landscape"
              style={styles.backPage}
              wrap={false}
            >
              {/* BACK PAGE HEADER */}

              <View style={styles.backPageHeader}>
                <View style={styles.backPageHeaderLeft}>
                  <Image src={logo} style={styles.backPageLogo} />

                  <View>
                    <Text style={styles.backPageTitle}>
                      STUDENT ACADEMIC PROFILE
                    </Text>

                    <Text style={styles.backPageSubtitle}>
                      Official School Record • Student Development • Promotion
                    </Text>
                  </View>
                </View>

                <Text style={styles.backPageTag}>STUDENT RECORD</Text>
              </View>

              <View style={styles.backColumns}>
                {/* LEFT COLUMN */}

                <View style={styles.backColumnLeft}>
                  {/* SCHOOL INFORMATION */}

                  <View style={styles.card}>
                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />
                      <Text style={styles.cardTitle}>
                        SCHOOL INFORMATION
                      </Text>
                    </View>

                    <View style={styles.schoolProfileContent}>
                      <Image src={logo} style={styles.schoolProfileLogo} />

                      <Text style={styles.schoolProfileName}>
                        Royal Priesthood Academy
                      </Text>

                      <Text style={styles.schoolProfileLocation}>
                        Zazay Community, Paynesville City,
                        {"\n"}Monrovia, Liberia
                      </Text>

                      <View style={styles.infoGrid}>
                        <View style={styles.infoGridItem}>
                          <Text style={styles.infoGridLabel}>
                            ACADEMIC YEAR
                          </Text>

                          <Text style={styles.infoGridValue}>
                            {selectedYear?.name || "N/A"}
                          </Text>
                        </View>

                        <View style={styles.infoGridItem}>
                          <Text style={styles.infoGridLabel}>
                            DIVISION
                          </Text>

                          <Text style={styles.infoGridValue}>
                            {division}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* STUDENT PROFILE */}

                  <View style={styles.card}>
                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />
                      <Text style={styles.cardTitle}>STUDENT PROFILE</Text>
                    </View>

                    <View style={styles.studentProfileRow}>
                      <Image src={studentImage} style={styles.avatar} />

                      <View style={styles.studentProfileDetails}>
                        <Text style={styles.profileStudentName}>
                          {data.student_name || "N/A"}
                        </Text>

                        <View style={styles.profileDetailRow}>
                          <Text style={styles.profileDetailLabel}>
                            Grade / Class:
                          </Text>

                          <Text style={styles.profileDetailValue}>
                            {data.grade_class_name || "N/A"}
                          </Text>
                        </View>

                        <View style={styles.profileDetailRow}>
                          <Text style={styles.profileDetailLabel}>
                            Academic Year:
                          </Text>

                          <Text style={styles.profileDetailValue}>
                            {selectedYear?.name || "N/A"}
                          </Text>
                        </View>

                        <View style={styles.profileDetailRow}>
                          <Text style={styles.profileDetailLabel}>
                            Class Rank:
                          </Text>

                          <Text style={styles.profileDetailValue}>
                            {data.rank || "N/A"}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* PERFORMANCE OVERVIEW */}

                  <View style={styles.card}>
                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />
                      <Text style={styles.cardTitle}>
                        PERFORMANCE OVERVIEW
                      </Text>
                    </View>

                    <View style={styles.averageSummaryRow}>
                      <View style={styles.averageSummaryItem}>
                        <View style={styles.averageSummaryCircle}>
                          <Text
                            style={[
                              styles.averageSummaryValue,
                              { color: getColor(data.semester_1_avg) },
                            ]}
                          >
                            {safeAverage(data.semester_1_avg)}
                          </Text>
                        </View>

                        <Text style={styles.averageSummaryLabel}>
                          SEMESTER 1
                        </Text>
                      </View>

                      <View style={styles.averageSummaryItem}>
                        <View style={styles.averageSummaryCircleOrange}>
                          <Text
                            style={[
                              styles.averageSummaryValue,
                              { color: getColor(data.semester_2_avg) },
                            ]}
                          >
                            {safeAverage(data.semester_2_avg)}
                          </Text>
                        </View>

                        <Text style={styles.averageSummaryLabel}>
                          SEMESTER 2
                        </Text>
                      </View>

                      <View style={styles.averageSummaryItem}>
                        <View style={styles.averageSummaryCircleGreen}>
                          <Text
                            style={[
                              styles.averageSummaryValue,
                              { color: getColor(data.yearly_average) },
                            ]}
                          >
                            {safeAverage(data.yearly_average)}
                          </Text>
                        </View>

                        <Text style={styles.averageSummaryLabel}>
                          FINAL YEARLY
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* STUDENT DEVELOPMENT */}

                  <View style={styles.card}>
                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />
                      <Text style={styles.cardTitle}>
                        STUDENT DEVELOPMENT
                      </Text>
                    </View>

                    <View style={styles.infoGrid}>
                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          YEARLY CONDUCT
                        </Text>

                        <Text style={styles.infoGridValue}>
                          {data.yearly_conduct || "N/A"}
                        </Text>
                      </View>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          CLASS RANK
                        </Text>

                        <Text style={styles.infoGridValue}>
                          {data.rank || "N/A"}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>

                {/* RIGHT COLUMN */}

                <View style={styles.backColumnRight}>
                  {/* PROMOTION DECISION */}

                  <View style={styles.card}>
                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />
                      <Text style={styles.cardTitle}>
                        PROMOTION DECISION
                      </Text>
                    </View>

                    {[
                      ["A", "Promoted to:"],
                      ["B", "Condition in:"],
                      ["C", "Fail / Repeat Grade Class"],
                      ["D", "Never to Return"],
                    ].map(([letter, label], index) => (
                      <View
                        key={letter}
                        style={[
                          styles.promotionOption,
                          index === 3 && {
                            borderBottomWidth: 0,
                          },
                        ]}
                      >
                        <View style={styles.promotionLetter}>
                          <Text style={styles.promotionLetterText}>
                            {letter}
                          </Text>
                        </View>

                        <Text style={styles.promotionOptionText}>
                          {label}
                        </Text>

                        {index < 2 && <View style={styles.promotionLine} />}
                      </View>
                    ))}
                  </View>

                  {/* PERIOD AVERAGES */}

                  <View style={styles.card}>
                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />
                      <Text style={styles.cardTitle}>PERIOD AVERAGES</Text>
                    </View>

                    {allPeriods.map((period) => (
                      <View key={period} style={styles.periodAverageRow}>
                        <Text style={styles.periodAverageName}>
                          {period}
                        </Text>

                        <Text
                          style={[
                            styles.periodAverageValue,
                            {
                              color: getColor(
                                data.period_average?.[period]
                              ),
                            },
                          ]}
                        >
                          {formatValue(data.period_average?.[period])}
                        </Text>
                      </View>
                    ))}
                  </View>

                  {/* FINAL ACADEMIC SUMMARY */}

                  <View style={styles.card}>
                    <View style={styles.cardHeader}>
                      <View style={styles.cardHeaderAccent} />
                      <Text style={styles.cardTitle}>
                        FINAL ACADEMIC SUMMARY
                      </Text>
                    </View>

                    <View style={styles.infoGrid}>
                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          SEMESTER 1 AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            { color: getColor(data.semester_1_avg) },
                          ]}
                        >
                          {safeAverage(data.semester_1_avg)}
                        </Text>
                      </View>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          SEMESTER 2 AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            { color: getColor(data.semester_2_avg) },
                          ]}
                        >
                          {safeAverage(data.semester_2_avg)}
                        </Text>
                      </View>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>
                          YEARLY AVERAGE
                        </Text>

                        <Text
                          style={[
                            styles.infoGridValue,
                            { color: getColor(data.yearly_average) },
                          ]}
                        >
                          {safeAverage(data.yearly_average)}
                        </Text>
                      </View>

                      <View style={styles.infoGridItem}>
                        <Text style={styles.infoGridLabel}>CLASS RANK</Text>

                        <Text style={styles.infoGridValue}>
                          {data.rank || "N/A"}
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* PARENT SIGNATURE */}

                  <View style={styles.parentSignatureCard}>
                    <Text style={styles.parentSignatureTitle}>
                      PARENT / GUARDIAN SIGNATURE
                    </Text>

                    <View style={styles.parentSignatureLine} />

                    <Text style={styles.parentSignatureCaption}>
                      Signature and Date
                    </Text>
                  </View>
                </View>
              </View>

              {/* BACK PAGE FOOTER */}

              <View style={styles.backFooter}>
                <Text style={styles.backFooterText}>
                  This report card is an official academic record of Royal
                  Priesthood Academy. Design By: Polu Integrated Technology -
                  School Management System.
                </Text>

                <Text style={styles.backFooterHighlight}>
                  EDUCATION • CHARACTER • EXCELLENCE
                </Text>
              </View>
            </Page>
          </React.Fragment>
        );
      })}
    </Document>
  );
};

export default ReportCardPDF;
